<template>
  <page-header title="Banner" :identifier="id">
    <template v-if="isLoaded">
      <button v-if="id" class="btn-outline-primary ml-auto h-10 mr-2" @click="toggleEnableDisableBanner">
        <template v-if="banner.isActive">
          <LockClosedIcon class="size-5 -ml-1 mr-2" />
          Disable
        </template>
        <template v-else>
          <LockOpenIcon class="size-5 -ml-1 mr-2" />
          Enable
        </template>
      </button>
      <button class="btn-primary ml-auto h-10" :class="{'disabled': isSaving || !isDataChanged}" @click="save">
        <CircleStackIcon class="size-5 -ml-1 mr-2" />
        Save
      </button>
    </template>
  </page-header>

  <pre v-if="false">
    {{ JSON.stringify(banner, null, 2) }}
  </pre>

  <banner-form v-if="isLoaded" ref="bannerFormRef" :banner="banner" @save="save" @update:background-type="updateBackgroundType" />

  <div v-else>
    Loading...
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, toRaw } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import PageHeader from '../components/PageHeader.vue';
import BannerForm from '../components/BannerForm.vue';
import {
    CircleStackIcon,
    LockOpenIcon,
    LockClosedIcon
  } from '@heroicons/vue/24/outline';
import { useFetch } from '@vueuse/core';
import { apiDomain } from '../composables/useConstants';
import { Modal } from '../composables/useModal';
import { useToast } from 'vue-toastification';
import hashSum from 'hash-sum';

const props = defineProps({
  id: {
    type: Number,
    default: null
  }
});

const mapPriority = {
  personalizedBannerForTheCategory: 1,
  noSubscription: 1,
  hourSubscription: 1,
  monthlySubscription: 1,
  newProgramInCategory: 2,
  newFeature: 2
};

const toast = useToast();
const bannerFormRef = ref(null);
const isLoaded = ref(false);
const isSaving = ref(false);
const banner = ref({
  title: null,
  content: null,
  actionButtonName: null,
  actionType: 'url',
  link: null,
  backgroundColor: '#ffffff',
  backgroundImage: '',
  textColor: '#000000',
  rightBlockImage: '',
  order: 0,
  conditions: null,
  isCategoryBanner: false,
  categoryId: null,
  isActive: false,
  priority: 1
});
const bannerHashSum = ref(null);

const backgroundType = ref('color');

const isDataChanged = computed(() => bannerHashSum.value !== hashSum(banner.value));

if (props.id) {
  load();
} else {
  isLoaded.value = true;
}

async function load() {
  const { data } = await useFetch(`${apiDomain}/api/banner/${props.id}`).get().json();
  bannerHashSum.value = hashSum(data.value);
  banner.value = data.value;
  isLoaded.value = true;
}

async function save() {
  isSaving.value = true;

  bannerFormRef.value.onValidate();
  if (bannerFormRef.value.getResult().$invalid) {
    isSaving.value = false;
    return;
  }

  banner.value.isActive = banner.value.isActive === true ? true : false;
  banner.value.priority = mapPriority[banner.value.conditions];

  const { data, error } = await useFetch(`${apiDomain}/api/banner/`).post(toRaw(banner.value)).json();

  isSaving.value = false;

  if (error.value) {
    toast.error(error.value);
  }

  if (data.value) {
    bannerHashSum.value = hashSum(data.value);
    banner.value = data.value;
    toast.success('Banner saved successfully');
  }
}

function updateBackgroundType(newType) {
  backgroundType.value = newType;
}

async function toggleEnableDisableBanner() {
  const direction = banner.value.isActive ? 'disable' : 'enable';
  const { data } = await useFetch(`${apiDomain}/api/banner/${direction}/${banner.value.id}`).get().json();

  if (data.value?.success) {
    load();
    toast.success(`Program ${direction}d successfully`);
    return;
  }

  toast.error('Error enabling banner');
}

function handleBeforeUnload(e) {
  if (!isDataChanged.value || !Object.keys(banner.value).length) {
    return;
  }
  e.preventDefault();
  e.returnValue = '';
}

onBeforeRouteLeave(async (to, from, next) => {
  if (!isDataChanged.value || !Object.keys(banner.value).length) {
    return next();
  }

  const confirm = await Modal.confirm({
    title: 'Unsaved changes',
    body: 'You have unsaved changes. <br/> If you leave this page, your changes will be lost. <br/> Are you sure you want to exit without saving?',
    okButton: 'Leave',
    iconColor: 'primary'
  });

  return confirm ? next() : next(false);
});

onMounted(() => {
  window.addEventListener('beforeunload', handleBeforeUnload);
});

onBeforeUnmount(() => {
  window.removeEventListener('beforeunload', handleBeforeUnload);
});
</script>
